/**
 * Zelium AI — Theme Provider
 * ===========================
 * Wraps the app in the `.zelium` scope class so all CSS custom properties
 * from zelium-tokens.css are available to every descendant.
 *
 * Also provides:
 * - Reduced motion detection via `useReducedMotion()` hook
 * - Theme context for components to access motion/theme state
 *
 * @see documents/ui-enhancement-spec.md §9.4, §17
 */

import React, { createContext, useContext, useMemo } from 'react';
import { useReducedMotion } from '../hooks/useReducedMotion';
import { MOTION, type MotionConfig } from './theme.config';

interface ZeliumThemeContextValue {
  /** Whether the user prefers reduced motion */
  reducedMotion: boolean;
  /** Motion config — durations are zeroed when reducedMotion is true */
  motion: MotionConfig;
}

const ZeliumThemeContext = createContext<ZeliumThemeContextValue>({
  reducedMotion: false,
  motion: MOTION,
});

export function useZeliumTheme(): ZeliumThemeContextValue {
  return useContext(ZeliumThemeContext);
}

interface ZeliumThemeProviderProps {
  children: React.ReactNode;
}

/**
 * Wrap the entire app in this provider (inside Polaris AppProvider).
 * It adds the `.zelium` class for CSS token scoping and provides
 * motion-aware context to all descendants.
 */
export function ZeliumThemeProvider({ children }: ZeliumThemeProviderProps): React.ReactElement {
  const reducedMotion = useReducedMotion();

  const value = useMemo<ZeliumThemeContextValue>(() => {
    if (reducedMotion) {
      return {
        reducedMotion: true,
        motion: {
          ...MOTION,
          duration: {
            instant: 0,
            fast: 0,
            normal: 0,
            slow: 0,
            dramatic: 0,
          },
        },
      };
    }
    return { reducedMotion: false, motion: MOTION };
  }, [reducedMotion]);

  return (
    <ZeliumThemeContext.Provider value={value}>
      <div className="zelium">
        {children}
      </div>
    </ZeliumThemeContext.Provider>
  );
}
